import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const LoadingScreen = () => {
  const [show, setShow] = useState(true);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setProgress((p) => {
        if (p >= 100) { clearInterval(interval); return 100; }
        return p + Math.random() * 15 + 5;
      });
    }, 100);
    const timer = setTimeout(() => setShow(false), 2200);
    return () => { clearTimeout(timer); clearInterval(interval); };
  }, []);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          className="fixed inset-0 z-[9999] bg-background flex items-center justify-center overflow-hidden"
          exit={{ opacity: 0, scale: 1.02, filter: "blur(10px)" }}
          transition={{ duration: 0.8, ease: [0.25, 0.4, 0.25, 1] }}
        >
          {/* Mesh gradient background */}
          <div className="absolute inset-0 mesh-gradient" />
          <div className="absolute inset-0 bg-diagonal opacity-30" />
          
          {/* Radial glow */}
          <motion.div
            className="absolute w-96 h-96 rounded-full"
            style={{ background: "radial-gradient(circle, hsl(43 72% 52% / 0.12) 0%, transparent 70%)" }}
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: [0, 3.5, 3], opacity: [0, 0.8, 0.4] }}
            transition={{ duration: 2.2, ease: "easeOut" }}
          />

          {/* Orbiting rings */}
          {[0, 1, 2].map(i => (
            <motion.div
              key={i}
              className="absolute rounded-full border"
              style={{
                width: `${120 + i * 60}px`,
                height: `${120 + i * 60}px`,
                borderColor: `hsl(43 72% 52% / ${0.06 - i * 0.015})`,
              }}
              initial={{ scale: 0, rotate: 0 }}
              animate={{ scale: 1, rotate: 360 }}
              transition={{
                scale: { duration: 0.8, delay: 0.2 + i * 0.15, ease: "easeOut" },
                rotate: { duration: 12 + i * 4, repeat: Infinity, ease: "linear" },
              }}
            />
          ))}

          {/* Floating particles */}
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={`p-${i}`}
              className="absolute w-1 h-1 rounded-full bg-primary/20"
              style={{
                top: `${30 + Math.random() * 40}%`,
                left: `${30 + Math.random() * 40}%`,
              }}
              animate={{
                y: [0, -20, 0],
                x: [0, Math.random() > 0.5 ? 10 : -10, 0],
                opacity: [0, 0.8, 0],
              }}
              transition={{
                duration: 2 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 1.5,
              }}
            />
          ))}

          <div className="relative flex flex-col items-center">
            {/* Logo mark */}
            <motion.div
              className="w-16 h-16 rounded-2xl gold-gradient-bg flex items-center justify-center mb-6 gold-glow-strong"
              initial={{ opacity: 0, scale: 0, rotate: -45 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ duration: 0.6, delay: 0.1, type: "spring", stiffness: 200 }}
            >
              <span className="font-display text-2xl font-bold text-primary-foreground">M</span>
            </motion.div>

            {/* Logo text */}
            <motion.div
              className="font-display text-3xl md:text-4xl font-bold tracking-[0.15em]"
              initial={{ opacity: 0, y: 10, filter: "blur(10px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              transition={{ duration: 0.8, delay: 0.3, ease: [0.25, 0.4, 0.25, 1] }}
            >
              <span className="gold-gradient-text-animated">METRO</span>
              <span className="text-foreground">SHINE</span>
            </motion.div>

            {/* Progress bar */}
            <div className="w-40 h-[2px] bg-border/10 mt-8 rounded-full overflow-hidden">
              <motion.div
                className="h-full rounded-full"
                style={{ background: "linear-gradient(90deg, hsl(var(--gold-dark)), hsl(var(--gold-light)))" }}
                initial={{ width: 0 }}
                animate={{ width: `${Math.min(progress, 100)}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>

            {/* Tagline */}
            <motion.p
              className="text-muted-foreground/60 text-[9px] tracking-[0.5em] uppercase mt-6 font-body"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.8 }}
            >
              Premium Mobile Detailing
            </motion.p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LoadingScreen;
